import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Target, Shield, Award } from 'lucide-react';

const About = () => {
  const values = [
    { icon: Shield, title: 'Safety First', desc: 'Strict adherence to safety protocols in every project' },
    { icon: Award, title: 'Quality Excellence', desc: 'Precision engineering with highest quality standards' },
    { icon: Target, title: 'Timely Delivery', desc: 'Commitment to project deadlines without compromise' },
    { icon: Building2, title: 'Industrial Expertise', desc: 'Deep understanding of heavy industrial requirements' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-block border-l-4 border-orange-500 pl-4 mb-4">
              <p className="text-orange-500 font-oswald tracking-widest text-sm">ABOUT US</p>
            </div>
            <h1 className="font-oswald font-bold text-5xl md:text-6xl mb-6">STRUCTURE BUILT ON TRUST</h1>
            <p className="text-xl text-slate-300">Since 2007</p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 bg-white" data-testid="company-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-oswald font-bold text-4xl text-slate-900 mb-6">OUR JOURNEY</h2>
              <div className="space-y-4 text-slate-700">
                <p>
                  Established in 2007, <strong>Durga Engineering Works</strong> has grown from humble beginnings 
                  into one of Eastern India's most trusted names in structural steel fabrication and industrial erection.
                </p>
                <p>
                  What started with a small dedicated team has evolved into a 300+ workforce company, executing 
                  large-scale mechanical construction projects across ferro alloy, sponge iron, and steel industries.
                </p>
                <p>
                  Our annual turnover of approximately ₹7.5 Crores and fabrication capacity of 500 MT/Month 
                  (Heavy + Rolled Section) stand testament to our consistent growth and client trust.
                </p>
                <p>
                  With projects spanning from <strong>Guwahati International Airport</strong> to major industrial 
                  units across India, we have successfully executed over 3000 MT of structural steel work, 
                  maintaining our reputation for precision, safety, and reliability.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1618088494676-ef512dbfc8a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwc3RlZWwlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwd2VsZGluZyUyMGZhYnJpY2F0aW9uJTIwY3JhbmV8ZW58MHx8fHwxNzcyMTc2NDQ4fDA&ixlib=rb-4.1.0&q=85"
                alt="Industrial Construction"
                className="w-full h-[500px] object-cover border-l-4 border-orange-500 shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership Section - Combined Founder & Deputy */}
      <section className="py-24 bg-slate-50" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-oswald font-bold text-4xl text-slate-900 mb-2">LEADERSHIP & VISION</h2>
            <div className="w-20 h-1 bg-orange-500 mx-auto"></div>
          </div>

          {/* Founder */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="lg:col-span-1"
              >
                <div className="relative mx-auto max-w-sm">
                  <div className="absolute inset-0 bg-orange-500 transform translate-x-4 translate-y-4"></div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_erection-expert/artifacts/5cbr7qow_IMG_5723.jpg"
                    alt="Mr. Durgadas Ghosh"
                    className="relative w-full h-auto border-4 border-white shadow-xl"
                    data-testid="founder-image"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="lg:col-span-2"
              >
                <div className="bg-white p-8 border-l-4 border-orange-500 shadow-md">
                  <h3 className="font-oswald font-bold text-3xl text-slate-900 mb-2">Mr. Durgadas Ghosh</h3>
                  <p className="text-orange-500 font-oswald text-sm tracking-wider mb-6">FOUNDER & PROPRIETOR</p>
                  
                  <div className="space-y-4 text-slate-700">
                    <p>
                      Mr. Durgadas Ghosh established Durga Engineering Works in 2007 with a vision to deliver 
                      structurally sound and technically reliable mechanical construction solutions to India's 
                      growing industrial sector.
                    </p>
                    <p>
                      Under his leadership and unwavering commitment to quality, the company has grown into a 
                      300+ workforce contractor, executing large-scale structural projects across ferro alloy, 
                      steel, and sponge iron industries.
                    </p>
                    <p>
                      His hands-on approach and deep understanding of industrial requirements have earned the 
                      company long-term partnerships with leading EPC contractors and industrial units across 
                      Eastern India.
                    </p>
                    
                    <div className="border-l-4 border-slate-300 pl-6 py-4 mt-6 italic text-slate-600">
                      <p className="text-lg">
                        "We don't just fabricate structures — we build long-term industrial partnerships 
                        based on trust, precision, and unwavering commitment to safety."
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Deputy Project Contractor */}
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="lg:col-span-1"
              >
                <div className="relative mx-auto max-w-sm">
                  <div className="absolute inset-0 bg-orange-500 transform translate-x-4 translate-y-4"></div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_erection-expert/artifacts/6rxpa1og_ChatGPT%20Image%20Dec%2030%2C%202025%20at%2006_30_54%20PM%202.PNG"
                    alt="Dip Ghosh"
                    className="relative w-full h-auto border-4 border-white shadow-xl"
                    data-testid="deputy-image"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="lg:col-span-2"
              >
                <div className="bg-white p-8 border-l-4 border-orange-500 shadow-md">
                  <h3 className="font-oswald font-bold text-3xl text-slate-900 mb-2">DIP GHOSH</h3>
                  <p className="text-orange-500 font-oswald text-sm tracking-wider mb-6">DEPUTY PROJECT CONTRACTOR</p>
                  
                  <div className="space-y-4 text-slate-700 mb-6">
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                      <span><strong>Structural & Mechanical Works</strong></span>
                    </div>
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                      <span><strong>EPC Coordination</strong></span>
                    </div>
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                      <span><strong>Steel & Industrial Projects</strong></span>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 border-l-4 border-slate-300">
                    <p className="text-sm text-slate-600 mb-1"><strong>Qualification:</strong></p>
                    <p className="text-slate-900 font-oswald text-lg">B.Tech – NIT Durgapur (2026)</p>
                  </div>

                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <p className="text-slate-700">
                      Leading technical coordination and project execution with expertise in structural steel fabrication, 
                      mechanical construction, and EPC project management. Brings modern engineering practices and 
                      innovation to traditional industrial construction methods.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-oswald font-bold text-4xl text-slate-900 mb-4">OUR CORE VALUES</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              The principles that drive every project we undertake
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 p-6 border-l-4 border-orange-500 hover:shadow-lg transition-shadow"
                data-testid={`value-${index}`}
              >
                <value.icon className="w-12 h-12 text-orange-500 mb-4" />
                <h3 className="font-oswald font-bold text-xl text-slate-900 mb-2">{value.title}</h3>
                <p className="text-sm text-slate-600">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;